/**
 * A form's responses: every answer to it across chats, read from chat-kit
 * through the BFF's /api/forms/responses. Ported from the mock's
 * `domain/form/responseRules` and `application/form/useFormSubmissions`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormResponseDto } from '@chat-emp/shared';

import type { AnsweredForm, FormField, FormId } from '../fixtures/domain.js';
import { listFormResponses } from '../lib/facade.js';
import { answerValues } from './forms.js';

/** chat-kit's record of one answered form. It outlives the chat it came from,
 *  so `channelCid` may name a chat that no longer exists. */
export interface FormSubmission extends AnsweredForm {
  channelCid: string;
  jobseekerId: string;
  /** When the form went into the chat. */
  sentAt: string;
  jobId?: string;
  jobTitle?: string;
}

export interface ResponseProfile {
  expectedSalary?: number;
  city?: string;
  education?: string;
  languages: string[];
}

export interface ResponsePerson {
  id: string;
  name?: string;
  profile?: ResponseProfile;
}

/** One answer with who gave it. */
export interface ResponseRow {
  submission: FormSubmission;
  person: ResponsePerson;
}

/** Read against the snapshot that was SENT, matched by field id: null when a
 *  question was added after this was answered, so it was never asked. */
export function answersTo(submission: FormSubmission, field: FormField): string[] | null {
  const asked = submission.notice.fields.find((entry) => entry.id === field.id);
  return asked ? answerValues(asked, submission.response.answers) : null;
}

export function newestFirst<T extends AnsweredForm>(answered: T[]): T[] {
  return [...answered].sort((a, b) => b.response.submittedAt.localeCompare(a.response.submittedAt));
}

export function toResponseRow(dto: FormResponseDto): ResponseRow {
  const profile = dto.profile;
  return {
    submission: {
      notice: { formId: dto.formId, name: dto.formName, fields: dto.fields },
      response: {
        id: dto.submissionId,
        formId: dto.formId,
        // The message that asked is chat-kit's to know; nothing here reads it.
        noticeMessageId: '',
        answers: dto.answers,
        submittedAt: dto.submittedAt,
      },
      channelCid: dto.channelCid,
      jobseekerId: dto.jobseekerId,
      sentAt: dto.sentAt,
      ...(dto.jobId ? { jobId: dto.jobId } : {}),
      ...(dto.jobTitle ? { jobTitle: dto.jobTitle } : {}),
    },
    person: {
      id: dto.jobseekerId,
      ...(profile?.name ? { name: profile.name } : {}),
      ...(profile
        ? {
            profile: {
              ...(profile.expectedSalary !== null
                ? { expectedSalary: profile.expectedSalary }
                : {}),
              ...(profile.city ? { city: profile.city } : {}),
              ...(profile.education ? { education: profile.education } : {}),
              languages: profile.languages,
            },
          }
        : {}),
    },
  };
}

export interface FormResponses {
  rows: ResponseRow[];
  loading: boolean;
  failed: boolean;
  /** More answers exist than the newest 1,000 shown. */
  truncated: boolean;
  retry: () => void;
}

/** One read per form opened; `retry` asks again. */
export function useFormResponses(formId: FormId): FormResponses {
  const [state, setState] = useState({
    rows: [] as ResponseRow[],
    loading: true,
    failed: false,
    truncated: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true, failed: false }));
    listFormResponses({ formId })
      .then((result) => {
        if (!active) return;
        setState({
          rows: result.responses.map(toResponseRow),
          loading: false,
          failed: false,
          truncated: result.truncated,
        });
      })
      .catch(() => {
        if (active) setState({ rows: [], loading: false, failed: true, truncated: false });
      });
    return () => {
      active = false;
    };
  }, [formId, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  return { ...state, retry };
}
